import { CustomerType } from '@energy-retailer/constants';
import { z } from 'zod';

export enum ControlledLoad {
  CL1 = 1,
  CL2 = 2,
  COMBINED = 3,
}

export const residentialNTCs = [
  '3700',
  '3750',
  '3770',
  '3900',
  '3950',
  '3970',
  '6900',
  '6950',
  '6970',
  '7000',
  '8400',
  '8400-2',
  '8420',
  '8450',
  '8470',
  '8900',
  '8920',
  '8950',
  '8970',
  'A100',
  'A100A180',
  'A10I',
  'A10X',
  'A120',
  'A140',
  'BLND1AR',
  'BLNN2AU',
  'BLNN2AU-2',
  'BLNRSS2',
  'BLNT3AL',
  'BLNT3AU',
  'C13R',
  'C13RB',
  'C1R',
  'C1R-P',
  'C1RB',
  'C1RCT',
  'C2R',
  'C2RB',
  'C3R',
  'C3RB',
  'CRDS',
  'CRTOU',
  'D1',
  'D1CS',
  'D1CSDD1',
  'D1DD1',
  'D2',
  'D2CS',
  'D2CS-2',
  'D2DK',
  'D2DKOP',
  'D3',
  'D3CS',
  'D3CSHW',
  'D3HW',
  'D3HW-2',
  'EA010',
  'EA011',
  'EA025',
  'EA111',
  'EA115',
  'EA116',
  'F100',
  'F10I',
  'F10X',
  'F120',
  'FLVS1R',
  'FTOD',
  'FTODFLEX',
  'FURTOU',
  'LVS1R',
  'LVS1RDED',
  'LVS2R',
  'MRSR',
  'MRSRI',
  'MRSROPCL',
  'N70',
  'N70-2',
  'N705',
  'N71',
  'N72',
  'N73',
  'NAST11',
  'NAST11P',
  'NAST11S',
  'NAST13',
  'NAST14',
  'NC01',
  'NC02',
  'NC02-1',
  'NEE11',
  'NEE11P',
  'NEE11S',
  'NEE13',
  'NEE14',
  'NEE15',
  'NEE20',
  'NEE23',
  'NEE24',
  'NEE26',
  'NFT9',
  'NG70',
  'NGT11',
  'NGT13',
  'NGT23',
  'NGT24',
  'NGT26',
  'NS70',
  'NS75',
  'NSP23',
  'NSP23-2',
  'P13R',
  'P13RCS',
  'P1DK',
  'PRDS',
  'PRTOU',
  'QRSR',
  'QRSRI',
  'QRSROPCL',
  'QRSROPCL-1',
  'RELE',
  'RELECL',
  'RSR',
  'RSRCL',
  'RSRCLMC',
  'RSROPCL',
  'RSROPCL-1',
  'RTOU',
  'RTOUCL',
  'RTOUCLMC',
  'RTOUMC',
  'RTOUPLUS',
  'SSP23',
  'SUN23',
  'SUN2T',
  'T100',
  'T10I',
  'T10X',
  'TLVS1R',
  'TOD',
  'TOD9',
  'TODFLEX',
  'TTOD',
  'TTODFLEX',
  'URDS',
  'URTOU',
  'URTOUDED',
  'WET2STEP',
  'WETDEDQ',
] as const;

export const businessNTCs = [
  '8500',
  '8500-2',
  '8520',
  '8550',
  '8570',
  'A200',
  'A200A180',
  'A210',
  'A250',
  'BLNN1AU',
  'BSR',
  'BSROPCL',
  'C14G',
  'C14GB',
  'C1G',
  'C1G-E',
  'C1G-E2',
  'C1G-E3',
  'C1GB',
  'C1GB-2',
  'C2G5',
  'C2G5B',
  'C2G5PK',
  'C2G7',
  'C2G7B',
  'C3G',
  'C3GB',
  'CGTOU',
  'EA050',
  'LVL2R',
  'LVM1R',
  'LVM1RDED',
  'LVM2R5D',
  'LVM2R7D',
  'LVTOU',
  'MBSR',
  'MBSROPCL',
  'N90',
  'NAST12',
  'NAST12P',
  'NAST12S',
  'NC03',
  'ND1',
  'ND2',
  'ND3',
  'ND5',
  'NDTOU',
  'NEE12',
  'NEE12P',
  'NEE12S',
  'NEE16',
  'NEE17',
  'NEE21',
  'NEE25',
  'NEE27',
  'NEE28',
  'NEE40',
  'NEE51',
  'NEE60',
  'NS90',
  'NSP27',
  'P14G',
  'QBSR',
  'QBSROPCL',
  'SUN21',
  'TLVM1R',
] as const;

export const validNTC = [...residentialNTCs, ...businessNTCs] as const;

export const customerType = z.enum(validNTC).transform((value) => {
  if (businessNTCs.includes(value as (typeof businessNTCs)[number])) {
    return CustomerType.Business;
  }

  if (residentialNTCs.includes(value as (typeof residentialNTCs)[number])) {
    return CustomerType.Residential;
  }

  throw new Error(`Invalid NTC: ${value}`);
});

export const ntcSchema = z.string().transform((value) => {
  const fullNTC = value;

  // NTC value may include both regular & controlled load
  // split the string into an array and remove any empty strings
  const [ntc, ...clNTCarray] = fullNTC
    .split(':')
    .filter((value) => value !== '');

  const clNTC = clNTCarray.map((cl) => {
    // handle Energex CL NTCs
    if (['9020', '9050', '9070'].includes(cl)) {
      return '9000';
    }

    if (['9120', '9150', '9170'].includes(cl)) {
      return '9100';
    }

    // Handle SA RELE CL
    if (['RELE'].includes(cl)) {
      return 'OPCL';
    }

    return cl;
  });

  return {
    controlledLoad: clNTC.length > 0,
    ntc,
    ntcs: [ntc],
    clntcs: clNTC.length > 0 ? clNTC : undefined,
  };
});

// CL NTCs are not used to distinguish between Residential and Business Meters,
// as most CL NTCs are used for both Residential and Business addresses.
export const validCLNTC = [
  '9000',
  '9020',
  '9050',
  '9070',
  '9100',
  '9120',
  '9150',
  '9170',
  'A180',
  'BLNC1AU',
  'BLNC2AU',
  'BSR',
  'CDS',
  'C2RBOP',
  'C2ROP',
  'DD1',
  'D2OP',
  'EA030',
  'EA040',
  'LVDED',
  'N50',
  'N54',
  'NEE30',
  'OPCL',
  'RSR',
  'RTOU',
  // Will end up being mapped to OPCL
  'RELE',
];

export const ntcMapping = {
  '3700': 'Single rate demand',
  '3900': 'Single rate transitional demand',
  '6900': 'Time of use energy',
  '8400': 'Single rate',
  '8500': 'Single rate',
  '8900': 'Time of use',
  A100: 'Single rate',
  A120: 'Time of use',
  A200: 'Single rate',
  A210: 'Time of use',
  BLNN1AU: 'Single rate',
  BLNN2AU: 'Single rate',
  BLNRSS2: 'Sun soaker time of use interval',
  BLNT3AL: 'Time of use interval',
  BLNT3AU: 'Time of use',
  BSR: 'Single rate',
  C1G: 'Single rate',
  C1R: 'Single rate',
  CGTOU: 'Time of use',
  CRDS: 'Flexible Pricing',
  CRTOU: 'Time of use',
  D1: 'Single rate',
  EA010: 'Single rate',
  EA011: 'Time of use transitional',
  EA025: 'Time of use',
  EA050: 'Single rate',
  EA111: 'Time of use transitional demand',
  EA115: 'Time of use demand',
  EA116: 'Single rate demand',
  LVM1R: 'Single rate',
  LVS1R: 'Single rate',
  LVTOU: 'Time of use',
  N705: 'Time of use interval',
  N70: 'Single rate',
  N71: 'Time of use seasonal',
  N72: 'Single rate demand',
  N73: 'Single rate demand',
  N90: 'Single rate',
  NAST11: 'Time of use',
  NAST12: 'Time of use',
  ND1: 'Single rate',
  NDTOU: 'Time of use',
  NEE11: 'Single rate',
  NEE12: 'Single rate',
  PRDS: 'Flexible Pricing',
  PRTOU: 'Time of use',
  RELE: 'Time of use electrify',
  RSR: 'Single rate',
  RTOU: 'Time of use',
  URDS: 'Flexible Pricing',
  URTOU: 'Time of use',
};

export const TOUTariffCodes = [
  '6900',
  '8900',
  'CGTOU',
  'CRTOU',
  'EA011',
  'EA025',
  'LVTOU',
  'N705',
  'N71',
  'NAST11',
  'NAST12',
  'NDTOU',
  'NAST11',
  'NAST12',
  'PRTOU',
  'RELE',
  'RTOU',
  'URTOU',
];

export const controlledLoadNTCMapping = {
  '9000': 'controlled load 1',
  '9020': 'controlled load 1',
  '9050': 'controlled load 1',
  '9070': 'controlled load 1',
  '9100': 'controlled load 2',
  '9120': 'controlled load 2',
  '9150': 'controlled load 2',
  '9170': 'controlled load 2',
  A180: 'controlled load',
  BLNC1AU: 'controlled load 1',
  BLNC2AU: 'controlled load 2',
  CDS: 'controlled load',
  C2RBOP: 'controlled load',
  C2ROP: 'controlled load',
  DD1: 'controlled load',
  D2OP: 'controlled load',
  EA030: 'controlled load 1',
  EA040: 'controlled load 2',
  LVDED: 'controlled load',
  N50: 'controlled load 1',
  N54: 'controlled load 2',
  NEE30: 'controlled load',
  OPCL: 'controlled load time of use',
  RELE: 'controlled load',
  RTOU: 'controlled load time of use',
  RSR: 'controlled load',
  BSR: 'controlled load',
};

export const mapNTC = (
  ntc: keyof typeof ntcMapping,
  clntc?: (keyof typeof controlledLoadNTCMapping)[],
  cl?: ControlledLoad
): string | null => {
  const mappedNTC = ntcMapping[ntc] as string | undefined;

  if (!mappedNTC) {
    return null;
  }

  // if this is not a controlled load tariff, return the NTC Tariff
  if (!clntc || clntc.length === 0) {
    return mappedNTC;
  }

  if (cl === ControlledLoad.COMBINED) {
    if (clntc.length > 1) {
      // if this is a combined tariff, return "with controlled load"
      // as opposed to distinguishing between CL1 and CL2
      return `${mappedNTC} with controlled load`;
    }

    // if this is combined but we only have one CLNTC, return null
    return null;
  }

  const mappedCL = controlledLoadNTCMapping[clntc[0]];

  return `${mappedNTC} with ${mappedCL}`;
};

export const smartMeterInstallationTypesVIC = ['MRIM', 'COMMS4D'];

export const smartMeterInstallationTypesAER = [
  'COMMS4',
  'COMMS4D',
  'COMMS4C',
  'COMMS4A',
  'MRAM',
];

export const evSupportingNTCs = [
  'EA025',
  'EA115',
  'N705',
  'NS75',
  'N71',
  'BLND1AR',
  'BLNT3AL',
  'BLNT3AU',
  '6900',
  '6950',
  '6970',
  '8900',
  '8920',
  '8950',
  '8970',
  'RELE',
  'RELECL',
  'RTOUPLUS',
  'RTOU',
  'RTOUCL',
  'RTOUCLMC',
  'RTOUMC',
  'NAST11',
  'NAST11P',
  'NAST11S',
  'NAST13',
  'NAST14',
  'NEE20',
  'NEE23',
  'NEE24',
  'NEE26',
  'NGT23',
  'NGT24',
  'NGT26',
  'NSP23',
  'NSP23-2',
  'SSP23',
  'SUN23',
  'SUN2T',
  'CRDS',
  'C13R',
  'C13RB',
  'C2R',
  'C2RB',
  'C3R',
  'C3RB',
  'CRTOU',
  'A10I',
  'A10X',
  'A120',
  'A140',
  'F10I',
  'F10X',
  'F120',
  'T10I',
  'T10X',
  'PRDS',
  'D2',
  'D2DKOP',
  'D3',
  'D3HW',
  'D3HW-2',
  'P13R',
  'PRTOU',
  'URDS',
  'FTOD',
  'FTODFLEX',
  'FURTOU',
  'LVS2R',
  'TOD',
  'TOD9',
  'TODFLEX',
  'TTOD',
  'TTODFLEX',
  'URTOU',
  'URTOUDED',
  'BLNRSS2',
];
